import { useEffect, useState } from 'react';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Minimum delay before the next scheduled refresh — guards against a zero/negative timeout. */
const MIN_REFRESH_DELAY_MS = 1000;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current date, automatically refreshed at the next UTC midnight rollover.
 * Useful for keeping UTC-calendar-day-scoped queries (KPIs, reports) in sync when a page is
 * left open across the day boundary — consumers that derive a query key from this value will
 * naturally refetch once the returned date rolls over to the next day.
 */
export function useUtcToday(): Date {
  const [today, setToday] = useState<Date>(() => new Date());

  useEffect(() => {
    const delay = getMsUntilNextUtcMidnight(today);
    const timer = setTimeout(() => {
      setToday(new Date());
    }, delay);
    return (): void => {
      clearTimeout(timer);
    };
  }, [today]);

  return today;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Milliseconds remaining until the next UTC midnight, clamped to a small minimum. */
function getMsUntilNextUtcMidnight(now: Date): number {
  const nextUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return Math.max(nextUtcMidnight - now.getTime(), MIN_REFRESH_DELAY_MS);
}
